<template>
	<layout-admin>
		<!-- Kopfzeile -->
		<header class="bg-white shadow">
			<div class="py-6 px-4 sm:px-6 lg:px-8 sm:flex sm:items-center sm:justify-between gap-x-4">
				<div class="flex items-center gap-x-4">
					<h1 class="text-3xl font-bold leading-tight text-gray-900 flex-shrink-0">
						{{ __("Preise") }}
					</h1>

					<div class="min-w-0 flex-1 max-w-sm">
					<select
						class="block w-full truncate pr-8 border border-gray-300 rounded-md shadow-sm py-2 px-3 text-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500"
						:value="selectedUserLocationId"
						@change="onLocationChange"
					>
						<option :value="null">{{ __("Alle Preise") }}</option>
						<option
							v-for="loc in userLocations"
							:key="loc.id"
							:value="loc.id"
						>
							{{ loc.label }}
						</option>
					</select>
					</div>

					<select
						v-if="selectedUserLocationId"
						class="border border-gray-300 rounded-md shadow-sm py-2 px-3 text-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500 w-32"
						v-model="selectedRadius"
						@change="loadUserLocationFilterOptions(); loadUserLocationContent(1)"
					>
						<option v-for="option in radiusOptions" :key="option.id" :value="option.id">{{ option.name }}</option>
					</select>

					<label v-if="selectedUserLocationId" class="flex items-center gap-x-2 text-sm text-gray-700 flex-shrink-0 cursor-pointer">
						<ui-toggle v-model="showStorageColumns" />
						<span>{{ __("Storage & report") }}</span>
					</label>
				</div>

				<div class="flex items-center gap-x-2">
					<input
						type="search"
						v-model="search"
						:placeholder="__('Search')"
						class="block border border-gray-300 rounded-md shadow-sm py-2 px-3 text-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500 w-48"
						@input="handleSearch"
					/>

					<ui-button
						class="flex items-center flex-shrink-0"
						@click="openCreateModal()"
					>
						<font-awesome-icon :icon="['fas', 'plus']" class="mr-2 -ml-1 flex-shrink-0 h-5 w-5" />
						<span>{{ __("Neuer Preis") }}</span>
					</ui-button>

					<ui-button
						class="flex items-center flex-shrink-0"
						@click="openCreateMarketLocationModal()"
					>
						<font-awesome-icon :icon="['fas', 'plus']" class="mr-2 -ml-1 flex-shrink-0 h-5 w-5" />
						<span>{{ __("Marktstandort anlegen") }}</span>
					</ui-button>

					<ui-button
						class="flex items-center flex-shrink-0"
						@click="openCreatePriceSourceModal()"
					>
						<font-awesome-icon :icon="['fas', 'plus']" class="mr-2 -ml-1 flex-shrink-0 h-5 w-5" />
						<span>{{ __("Preisquelle anlegen") }}</span>
					</ui-button>

					<ui-button
						class="flex items-center flex-shrink-0"
						type="secondary"
						@click="showBatch = !showBatch"
					>
						<font-awesome-icon
							:icon="[showBatch ? 'fas' : 'fass', showBatch ? 'chevron-up' : 'list-check']"
							class="mr-2 -ml-1 flex-shrink-0 h-4 w-4"
						/>
						<span>{{ __("Batch-Erfassung") }}</span>
					</ui-button>
				</div>
			</div>
		</header>

		<!-- Batch-Erfassung -->
		<admin-local-price-batch v-if="showBatch" />

		<!-- Allgemeine Preistabelle -->
		<admin-table
			v-if="!selectedUserLocationId"
			:rows="rows"
			:cols="generalCols"
			:loading="loading"
			:sort-columns="sort"
			:filter-values="filters"
			:pagination="pagination"
			@sort-change="handleSortChange"
			@filter-change="handleFilterChange"
			@page-change="loadContent"
		>
			<template v-slot:row-actions="{ row }">
				<div class="flex items-center justify-end">
					<button @click="openCopyModal(row)" class="inline-flex items-center justify-center text-orange-500 hover:bg-gray-200 rounded-lg p-2">
						<font-awesome-icon :icon="['far', 'copy']" class="h-4 w-4" />
					</button>
					<button @click="openEditModal(row)" class="inline-flex items-center justify-center text-orange-500 hover:bg-gray-200 rounded-lg p-2">
						<font-awesome-icon :icon="['far', 'pen-to-square']" class="h-4 w-4" />
					</button>
					<button @click="openDeleteModal(row)" class="inline-flex items-center justify-center text-orange-500 hover:bg-gray-200 rounded-lg p-2">
						<font-awesome-icon :icon="['far', 'trash']" class="h-4 w-4" />
					</button>
				</div>
			</template>
		</admin-table>

		<!-- Umgebungspreise -->
		<admin-table
			v-else
			:rows="rows"
			:cols="userLocationCols"
			:loading="loading"
			:sort-columns="sort"
			:filter-values="filters"
			:pagination="pagination"
			@sort-change="handleSortChange"
			@filter-change="handleFilterChange"
			@page-change="loadUserLocationContent"
		>
			<template v-slot:cell-freight_cost_eur="{ row, value }">
				<ui-tooltip v-if="row.freight_cost_eur !== null" placement="top">
					<template v-slot:label>
						<span>{{ value }}</span>
					</template>

					<template v-slot:content>
						<div class="text-left space-y-1 p-1.5">
							<div v-if="row.distance_km !== null">Luftlinie: {{ Math.round(row.distance_km) }} km</div>
							<div v-if="row.road_distance_km !== null">Straße: {{ Math.round(row.road_distance_km) }} km</div>
						</div>
					</template>
				</ui-tooltip>

				<span v-else>—</span>
			</template>

			<template v-slot:cell-price_estimate="{ row, value }">
				<ui-tooltip v-if="row.location_mode === 'DAP' && row.price_estimate !== null" placement="top">
					<template v-slot:label>
						<span>{{ value }}</span>
					</template>

					<template v-slot:content>
						<div class="text-left space-y-1 p-1.5">
							<div class="flex justify-between gap-4"><span>Franko-Preis</span><span class="whitespace-nowrap">{{ eur(row.price_mt) }}</span></div>
							<div class="flex justify-between gap-4"><span>− Fracht</span><span class="whitespace-nowrap">{{ eur(row.freight_cost_eur) }}</span></div>
							<div class="flex justify-between gap-4 border-t border-gray-500 pt-1 mt-1 font-bold"><span>Frei-Fuhre-Preis</span><span class="whitespace-nowrap">{{ eur(row.price_estimate) }}</span></div>
						</div>
					</template>
				</ui-tooltip>

				<span v-else>{{ value }}</span>
			</template>

			<template v-slot:cell-price_estimate_net="{ row, value }">
				<ui-tooltip v-if="row.price_estimate_net !== null" placement="top">
					<template v-slot:label>
						<span>{{ value }}</span>
					</template>

					<template v-slot:content>
						<div class="text-left space-y-1 p-1.5">
							<div class="flex justify-between gap-4"><span>Frei-Fuhre-Preis</span><span class="whitespace-nowrap">{{ eur(row.price_estimate) }}</span></div>
							<div class="flex justify-between gap-4"><span>− Lager</span><span class="whitespace-nowrap">{{ eur(row.storage_cost_eur) }}</span></div>
							<div class="flex justify-between gap-4"><span>− Abholzeit</span><span class="whitespace-nowrap">{{ eur(row.storage_cost_pickup_eur) }}</span></div>
							<div class="flex justify-between gap-4"><span>+ Report</span><span class="whitespace-nowrap">{{ eur(row.report_payment_eur) }}</span></div>
							<div class="flex justify-between gap-4 border-t border-gray-500 pt-1 mt-1 font-bold"><span>Real-Preis</span><span class="whitespace-nowrap">{{ eur(row.price_estimate_net) }}</span></div>
						</div>
					</template>
				</ui-tooltip>

				<span v-else>{{ value }}</span>
			</template>

			<template v-slot:row-actions="{ row }">
				<div class="flex items-center justify-end">
					<button
						v-if="row.freight_cost_eur === null && row.location_mode === 'DAP'"
						class="inline-flex items-center justify-center text-orange-500 hover:bg-gray-200 rounded-lg p-2"
						:title="__('Frachtkosten berechnen')"
						@click="calculateDistance(row)"
					>
						<font-awesome-icon :icon="['far', 'route']" class="h-4 w-4" />
					</button>
					<button
						class="inline-flex items-center justify-center text-orange-500 hover:bg-gray-200 rounded-lg p-2"
						@click="openEditModal(row)"
					>
						<font-awesome-icon :icon="['far', 'pen-to-square']" class="h-4 w-4" />
					</button>
					<button
						class="inline-flex items-center justify-center text-orange-500 hover:bg-gray-200 rounded-lg p-2"
						@click="openDeleteModal(row)"
					>
						<font-awesome-icon :icon="['far', 'trash']" class="h-4 w-4" />
					</button>
				</div>
			</template>
		</admin-table>
	</layout-admin>
</template>

<script>
import { debounce } from "lodash";
import { formatDate, formatMonth } from "../../utils/date";

// Lagerkosten-Spaltengruppe der Standortansicht, per Toggle ausblendbar (Einstellung je Browser).
const STORAGE_COLUMN_IDS = ["storage_cost_eur", "storage_cost_pickup_eur", "report_payment_eur", "price_estimate_net"];
const STORAGE_COLUMNS_KEY = "admin.local_prices.show_storage_columns";

/**
 * @return {boolean} gespeicherte Toggle-Einstellung, sonst eingeblendet
 */
function readShowStorageColumns() {
	try {
		return localStorage.getItem(STORAGE_COLUMNS_KEY) !== "0";
	} catch (e) {
		return true;
	}
}

export default {
	props: {
		productGrades:             { type: Array, required: true },
		userLocations:             { type: Array, required: true },
		dateTypes:                 { type: Array, required: true },
		locationModes:             { type: Array, required: true },
		harvestYears:              { type: Array, required: true },
		marketLocations:           { type: Array, required: true },
		priceSources:              { type: Array, required: true },
		radiusOptions:             { type: Array, required: true },
		defaultRadius:             { type: Number, required: true },
		locale:                    { type: String, required: true },
		initialUserLocationId:     { type: Number, default: null },
	},

	data() {
		return {
			selectedUserLocationId: this.initialUserLocationId,
			selectedRadius: this.defaultRadius,
			userLocationFilterOptions: { productGrades: [], marketLocations: [] },
			showBatch: false,
			loading: true,
			rows: [],
			pagination: { currentPage: 1, lastPage: 1, total: 0 },
			sort: [],
			showStorageColumns: readShowStorageColumns(),
			filters: {},
			search: "",
		};
	},

	computed: {
		generalCols() {
			return [
				{
					id: "id",
					name: this.__("ID"),
				},
				{
					id: "date",
					name: this.__("Datum"),
					filterType: "date",
					filterPlaceholder: "Datum",
					sortable: true,
					render: (v) => formatDate(v, { locale: this.locale }) ?? "—",
				},
				{
					id: "product__grade",
					name: this.__("Quality Grade"),
					render: (v) => {
						if (!v) return "—";
						const [grade, protein] = v.split("__");
						const label = this.__("product.grades.default." + grade);
						return protein ? `${label} (${this.__("product.proteins." + protein)})` : label;
					},
					filterOptions: this.productGrades.map((g) => {
						const raw              = g.name.replace("product.grades.long.", "");
						const [grade, protein] = raw.split("__");
						const label            = this.__("product.grades.default." + grade);

						return {
							id:   raw,
							name: protein ? `${label} (${this.__("product.proteins." + protein)})` : label,
						};
					}),
				},
				{
					id: "product_harvest",
					name: this.__("Erntejahr"),
					render: (v) => v ?? "—",
					filterOptions: this.harvestYears,
				},
				{
					id: "matif_kurs",
					name: this.__("Matif"),
					render: (v) => (v !== null ? v + " €" : "—"),
				},
				{
					id: "trading_month",
					name: this.__("Trading Month"),
					render: (v) => v
						? new Intl.DateTimeFormat(this.locale, { year: "2-digit", month: "short" }).format(new Date(v))
						: "—",
				},
				{
					id: "location_mode",
					name: this.__("Parität"),
					render: (v) => v ? this.__(v) : "—",
					sortable: false,
					filterOptions: this.locationModes.map((o) => ({ id: o.id, name: this.__(o.id) })),
				},
				{
					id: "market_location_id",
					name: this.__("Marktstandort"),
					render: (v, row) => row.market_location_city ? `${row.market_location_city} (${row.market_location_zip})` : "—",
					sortable: false,
					filterOptions: this.marketLocations.map((l) => ({
						id: l.id,
						name: l.label,
					})),
					filterDropdownClass: "origin-top-right absolute left-0 mt-2 w-82 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-500",
				},
				{
					id: "price_source_name",
					name: this.__("Preisquelle"),
					render: (v) => v || "—",
					filterOptions: this.priceSources.map((s) => ({ id: s.id, name: s.name })),
					filterDropdownClass: "origin-top-right absolute left-0 mt-2 w-82 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-500",
				},
				{
					id: "date_type",
					name: this.__("Termin"),
					render: (v) => {
						if (!v) return "—";
						const option = this.dateTypes.find((o) => o.id === v);
						return option ? this.__(option.name) : v;
					},
					filterOptions: this.dateTypes.map((o) => ({ id: o.id, name: this.__(o.name) })),
				},
				{
					id: "date_month_from",
					name: this.__("Von"),
					filterType: "month",
					filterPlaceholder: this.__("Start month"),
					sortable: true,
					render: (v) => formatMonth(v, { locale: this.locale }) ?? "—",
				},
				{
					id: "date_month_to",
					name: this.__("Bis"),
					filterType: "month",
					filterPlaceholder: this.__("Target month"),
					sortable: true,
					render: (v) => formatMonth(v, { locale: this.locale }) ?? "—",
				},
				{
					id: "price_mt",
					name: this.__("Preis"),
					render: (v) => v + " €",
				},
				{
					id: "price_basis",
					name: this.__("Prämie"),
					render: (v) => (v !== null ? v + " €" : "—"),
				},
				{
					id: "price_margin",
					name: this.__("Margin"),
					render: (v) => (v !== null ? v + " €" : "—"),
				},
			];
		},

		userLocationCols() {
			const cols = [
				{
					id: "id",
					name: this.__("ID"),
				},
				{
					id: "date",
					name: this.__("Datum"),
					filterType: "date",
					filterPlaceholder: "Datum",
					sortable: true,
					render: (v) => formatDate(v, { locale: this.locale }) ?? "—",
				},
				{
					id: "product__grade",
					name: this.__("Quality Grade"),
					render: (v) => {
						if (!v) return "—";
						const [grade, protein] = v.split("__");
						const label = this.__("product.grades.default." + grade);
						return protein ? `${label} (${this.__("product.proteins." + protein)})` : label;
					},
					filterOptions: this.userLocationFilterOptions.productGrades.map((raw) => {
						const [grade, protein] = raw.split("__");
						const label            = this.__("product.grades.default." + grade);

						return {
							id:   raw,
							name: protein ? `${label} (${this.__("product.proteins." + protein)})` : label,
						};
					}),
				},
				{
					id: "product_harvest",
					name: this.__("Erntejahr"),
					render: (v) => v ?? "—",
					filterOptions: this.harvestYears,
				},
				{
					id: "matif_kurs",
					name: this.__("Matif"),
					render: (v) => (v !== null ? v + " €" : "—"),
				},
				{
					id: "trading_month",
					name: this.__("Trading Month"),
					render: (v) => v
						? new Intl.DateTimeFormat(this.locale, { year: "2-digit", month: "short" }).format(new Date(v))
						: "—",
				},
				{
					id: "location_mode",
					name: this.__("Parität"),
					render: (v) => v ? this.__(v) : "—",
					sortable: false,
					filterOptions: this.locationModes.map((o) => ({ id: o.id, name: this.__(o.id) })),
				},
				{
					id: "market_location_id",
					name: this.__("Marktstandort"),
					render: (v, row) => row.market_location_city ? `${row.market_location_city} (${row.market_location_zip})` : "—",
					sortable: false,
					filterOptions: this.userLocationFilterOptions.marketLocations.map((l) => ({
						id: l.id,
						name: l.label,
					})),
					filterDropdownClass: "origin-top-right absolute left-0 mt-2 w-82 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-500",
				},
				{
					id: "price_source_name",
					name: this.__("Preisquelle"),
					render: (v) => v || "—",
					filterOptions: this.priceSources.map((s) => ({ id: s.id, name: s.name })),
					filterDropdownClass: "origin-top-right absolute left-0 mt-2 w-82 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-500",
				},
				{
					id: "date_type",
					name: this.__("Termin"),
					render: (v) => {
						if (!v) return "—";
						const option = this.dateTypes.find((o) => o.id === v);
						return option ? this.__(option.name) : v;
					},
					filterOptions: this.dateTypes.map((o) => ({ id: o.id, name: this.__(o.name) })),
				},
				{
					id: "date_month_from",
					name: this.__("Von"),
					filterType: "month",
					filterPlaceholder: this.__("Start month"),
					sortable: true,
					render: (v) => formatMonth(v, { locale: this.locale }) ?? "—",
				},
				{
					id: "date_month_to",
					name: this.__("Bis"),
					filterType: "month",
					filterPlaceholder: this.__("Target month"),
					sortable: true,
					render: (v) => formatMonth(v, { locale: this.locale }) ?? "—",
				},
				{
					id: "price_mt",
					name: this.__("Franko-Preis"),
					render: (v, row) => row.location_mode === "DAP" && v !== null ? v + " €" : "—",
				},
				{
					id: "price_basis",
					name: this.__("Franko-Prämie"),
					render: (v, row) => (row.location_mode === "DAP" && v !== null ? v + " €" : "—"),
				},
				{
					id: "freight_cost_eur",
					name: this.__("Fracht"),
					sortable: true,
					render: (v) => (v !== null ? v + " €" : "—"),
				},
				{
					id: "price_estimate",
					name: this.__("Frei-Fuhre-Preis"),
					sortable: true,
					render: (v) => (v !== null ? v + " €" : "—"),
				},
				{
					id: "price_estimate_basis",
					name: this.__("Frei-Fuhre-Prämie"),
					render: (v) => (v !== null ? Number(v).toFixed(2) + " €" : "—"),
				},
				{
					id: "storage_cost_eur",
					name: this.__("Lagerkosten"),
					sortable: true,
					render: (v) => (v !== null && Number(v) !== 0 ? Number(v).toFixed(2) + " €" : "—"),
				},
				{
					id: "storage_cost_pickup_eur",
					name: this.__("Lager in Abholzeit"),
					sortable: true,
					render: (v) => (v !== null && Number(v) !== 0 ? Number(v).toFixed(2) + " €" : "—"),
				},
				{
					id: "report_payment_eur",
					name: this.__("Report"),
					sortable: true,
					render: (v) => (v !== null && Number(v) !== 0 ? Number(v).toFixed(2) + " €" : "—"),
				},
				{
					id: "price_estimate_net",
					name: this.__("Real-Preis"),
					sortable: true,
					render: (v) => (v !== null ? Number(v).toFixed(2) + " €" : "—"),
				},
			];

			if (this.showStorageColumns) {
				return cols;
			}

			return cols.filter((col) => !STORAGE_COLUMN_IDS.includes(col.id));
		},
	},

	watch: {
		/**
		 * Einstellung merken; ausgeblendete Spalten fallen aus der Sortierung.
		 *
		 * @param {boolean} show
		 * @return {void}
		 */
		showStorageColumns(show) {
			try {
				localStorage.setItem(STORAGE_COLUMNS_KEY, show ? "1" : "0");
			} catch (e) {
				// Speicher nicht verfügbar (z.B. privates Fenster) — Einstellung gilt nur für diese Ansicht
			}

			if (show) {
				return;
			}

			const sort = this.sort.filter((s) => !STORAGE_COLUMN_IDS.includes(s.column));

			if (sort.length !== this.sort.length) {
				this.sort = sort;
				this.loadUserLocationContent(1);
			}
		},

		selectedUserLocationId(id) {
			this.rows       = [];
			this.sort       = [];
			this.filters    = {};
			this.search     = "";
			this.pagination = { currentPage: 1, lastPage: 1, total: 0 };

			if (id) {
				window.history.pushState(null, "", "/admin/prices/location/" + id);
				this.loadUserLocationFilterOptions();
				this.loadUserLocationContent(1);
			} else {
				this.userLocationFilterOptions = { productGrades: [], marketLocations: [] };
				window.history.pushState(null, "", "/admin/prices");
				this.loadContent(1);
			}
		},
	},

	mounted() {
		const page = parseInt(new URLSearchParams(window.location.search).get("page")) || 1;

		if (this.selectedUserLocationId) {
			this.loadUserLocationFilterOptions();
			this.loadUserLocationContent(page);
		} else {
			this.loadContent(page);
		}

		this.$bus.$on("content:load", this.onContentLoad);
	},

	beforeDestroy() {
		this.$bus.$off("content:load", this.onContentLoad);
	},

	methods: {
		/**
		 * Formatiert einen Betrag für die Tooltip-Herleitung (zwei Nachkommastellen + €).
		 *
		 * @param {number|string|null} v
		 * @return {string}
		 */
		eur(v) {
			return (v !== null && v !== undefined ? Number(v).toFixed(2) : "0.00") + " €";
		},

		/**
		 * @param {Object} row
		 * @return {void}
		 */
		calculateDistance(row) {
			axios
				.post(this.$route("admin.prices.location.calculate_distance", { userLocationId: this.selectedUserLocationId }), {
					market_location_id: row.market_location_id,
				})
				.then(() => {
					this.loadUserLocationContent(this.pagination.currentPage);
				})
				.catch((error) => {
					this.$bus.$emit("toast", { type: "error", message: error.response?.data?.message ?? this.__("Fehler bei der Berechnung.") });
				});
		},

		handleSearch: debounce(function () {
			if (this.search.length > 0 && this.search.length < 3) {
				return;
			}

			this.selectedUserLocationId ? this.loadUserLocationContent(1) : this.loadContent(1);
		}, 400),

		/**
		 * @param {Event} event
		 * @return {void}
		 */
		onLocationChange(event) {
			const val = event.target.value;
			this.selectedUserLocationId = val ? parseInt(val) : null;
		},

		/**
		 * Fetch distinct grade and market-location filter options for the current location/radius.
		 *
		 * @return {void}
		 */
		loadUserLocationFilterOptions() {
			if (!this.selectedUserLocationId) return;

			axios
				.get(this.$route("admin.prices.location.filter_options", { userLocationId: this.selectedUserLocationId }), {
					params: { radius: this.selectedRadius },
				})
				.then((response) => {
					this.userLocationFilterOptions = response.data;
				});
		},

		/**
		 * Reload current mode on content:load bus event.
		 *
		 * @return {void}
		 */
		onContentLoad() {
			if (this.selectedUserLocationId) {
				this.loadUserLocationContent(this.pagination.currentPage);
			} else {
				this.loadContent(this.pagination.currentPage);
			}
		},

		/**
		 * @param {number} page
		 * @return {void}
		 */
		loadContent(page = 1) {
			this.loading = true;

			axios
				.get(this.$route("admin.prices.data"), {
					params: {
						page,
						per_page: 50,
						sort:    JSON.stringify(this.sort),
						filters: JSON.stringify(this.filters),
						search:  this.search || undefined,
					},
				})
				.then((response) => {
					this.rows                   = response.data.data;
					this.pagination.currentPage = response.data.current_page;
					this.pagination.lastPage    = response.data.last_page;
					this.pagination.total       = response.data.total;
					window.history.pushState(null, "", "?page=" + response.data.current_page);
				})
				.finally(() => {
					this.loading = false;
				});
		},

		/**
		 * @param {number} page
		 * @return {void}
		 */
		loadUserLocationContent(page = 1) {
			this.loading = true;

			axios
				.get(this.$route("admin.prices.location.data", { userLocationId: this.selectedUserLocationId }), {
					params: {
						page,
						per_page: 50,
						radius:  this.selectedRadius,
						sort:    JSON.stringify(this.sort),
						filters: JSON.stringify(this.filters),
						search:  this.search || undefined,
					},
				})
				.then((response) => {
					this.rows                   = response.data.data;
					this.pagination.currentPage = response.data.current_page;
					this.pagination.lastPage    = response.data.last_page;
					this.pagination.total       = response.data.total;
					window.history.pushState(null, "", "/admin/prices/location/" + this.selectedUserLocationId);
				})
				.finally(() => {
					this.loading = false;
				});
		},

		/**
		 * @param {string} colId
		 * @return {void}
		 */
		handleSortChange(colId) {
			const idx = this.sort.findIndex((s) => s.column === colId);

			if (idx === -1) {
				this.sort.push({ column: colId, dir: "asc" });
			} else if (this.sort[idx].dir === "asc") {
				this.sort.splice(idx, 1, { column: colId, dir: "desc" });
			} else {
				this.sort.splice(idx, 1);
			}

			this.selectedUserLocationId ? this.loadUserLocationContent(1) : this.loadContent(1);
		},

		/**
		 * @param {{ colId: string, value: string|null }} param
		 * @return {void}
		 */
		handleFilterChange({ colId, value }) {
			if (value === null) {
				this.$delete(this.filters, colId);
			} else {
				this.$set(this.filters, colId, value);
			}

			this.selectedUserLocationId ? this.loadUserLocationContent(1) : this.loadContent(1);
		},

		/**
		 * @return {void}
		 */
		openCreateMarketLocationModal() {
			this.$bus.$emit("modal:open", {
				view: { view: "views/Admin/MarketLocationCreate", data: {} },
			});
		},

		/**
		 * @return {void}
		 */
		openCreatePriceSourceModal() {
			this.$bus.$emit("modal:open", {
				view: { view: "views/Admin/PriceSourceCreate", data: {} },
			});
		},

		/**
		 * @return {void}
		 */
		openCreateModal() {
			this.$bus.$emit("modal:open", {
				view: {
					view: "views/Admin/LocalPriceCreate",
					dataUrl: this.$route("admin.prices.dependencies"),
					data: {},
				},
			});
		},

		/**
		 * @param {Object} row
		 * @return {void}
		 */
		openCopyModal(row) {
			this.$bus.$emit("modal:open", {
				view: {
					view: "views/Admin/LocalPriceCreate",
					dataUrl: this.$route("admin.prices.dependencies", { id: row.id, copy: 1 }),
				},
			});
		},

		/**
		 * @param {Object} row
		 * @return {void}
		 */
		openEditModal(row) {
			this.$bus.$emit("modal:open", {
				view: {
					view: "views/Admin/LocalPriceCreate",
					dataUrl: this.$route("admin.prices.dependencies", { id: row.id }),
				},
			});
		},

		/**
		 * @param {Object} row
		 * @return {void}
		 */
		openDeleteModal(row) {
			this.$bus.$emit("modal:open", {
				view: { view: "views/Admin/LocalPriceDelete", data: { id: row.id } },
				size: "sm",
			});
		},
	},
};
</script>
